import { AddIcon, DropdownChevron } from '@components'
import { CUT_STRING_DEFAULT_SIZE } from '@constants'
import cn from '@integrations/classnames'
import { LoadingIndicator } from '@integrations/core'
import type { SelectOptions } from 'mmfintech-backend-api/types'
import { cutString, isValidString, tr, useDropDownMenu } from 'mmfintech-commons'
import { InputLabel } from 'mmfintech-portal-commons'
import styles from '../SelectAccount/SelectAccount.module.scss'
import surfaceStyles from '../SelectorSurface/SelectorSurface.module.scss'

interface Props {
  options: SelectOptions
  showAddButton: boolean
  value: string
  onChange: (name: string | null, value: string) => void
  label: string
  error?: string
  handleAdd: () => void
  loading?: boolean
  required?: boolean
  settlement?: boolean
  darkMode?: boolean
  staticDisplay?: boolean
  bordered?: boolean
}

function SelectRecipient({
  options,
  showAddButton,
  value,
  onChange,
  label,
  error,
  handleAdd,
  loading,
  required,
  settlement,
  darkMode,
  staticDisplay,
  bordered
}: Props) {
  const { visible, hideDropDown, toggleDropDown, setRef } = useDropDownMenu()
  function toggle() {
    !staticDisplay && toggleDropDown()
  }

  function handleSelect(value: string) {
    hideDropDown()
    onChange(null, value)
  }

  const selected = options.find((r) => Number(r.value) === Number(value))

  return (
    <div className={cn(surfaceStyles['selector-wrapper'], 'select-account-wrapper', { dark: darkMode })}>
      <InputLabel label={label} required={required} requiredPosition='right' />
      <div className={cn(surfaceStyles['selector-root'], 'select-account-styled')}>
        <div
          className={cn(surfaceStyles['selector-control'], styles['account-button-styled'], 'account-button-styled', {
            [styles['bordered']]: bordered,
            bordered,
            [surfaceStyles['static']]: staticDisplay,
            static: staticDisplay,
            [surfaceStyles['error']]: error?.length,
            error: error?.length
          })}
          ref={visible ? setRef : null}
          onClick={toggle}
        >
          {loading ? (
            <span className={cn(styles['loading'], 'loading')}>
              <LoadingIndicator variant='ThreeDots' height='12px' color='var(--banky-royal-blue)' />
            </span>
          ) : value ? (
            <span className={cn(surfaceStyles['selector-selection'], 'account-info', 'break-anywhere')}>
              <span
                className={cn(surfaceStyles['selector-selection-content'], styles['content'], 'content', 'recipient')}
              >
                {cutString(selected?.label, CUT_STRING_DEFAULT_SIZE)}
              </span>
            </span>
          ) : (
            <span className={cn(surfaceStyles['selector-placeholder'], styles['no-account'], 'no-account')}>
              {label}
            </span>
          )}
          {!staticDisplay && <DropdownChevron className={cn('chevron')} expanded={visible} />}
        </div>

        {!loading && !staticDisplay ? (
          <div
            className={cn(surfaceStyles['selector-menu'], 'account-list-styled', {
              [surfaceStyles['is-opened']]: visible,
              'is-opened': visible
            })}
            data-test='accounts-menu-content'
          >
            {Array.isArray(options) && options.length ? (
              options.map((recipient, index) => {
                const { value, label, description } = recipient
                return (
                  <div
                    className={cn(
                      surfaceStyles['selector-option'],
                      styles['account-styled'],
                      'account-styled',
                      'dropdown-option'
                    )}
                    key={index}
                    onClick={() => handleSelect(value)}
                    data-test='account-menu-item'
                  >
                    <div className={cn('custom-option-wrapper')}>
                      <div>{cutString(label, CUT_STRING_DEFAULT_SIZE)}</div>
                      {description && (
                        <div className={cn('custom-option-description')}>
                          {cutString(description, CUT_STRING_DEFAULT_SIZE)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            ) : Array.isArray(options) && !showAddButton ? (
              <div className={cn(styles['add-button-wrapper'], 'add-button-wrapper')}>
                <span>
                  {settlement
                    ? tr('FRONTEND.COMPONENTS.SELECT_RECIPIENT.NO_SETTLEMENT', 'No external accounts found')
                    : tr('FRONTEND.COMPONENTS.SELECT_RECIPIENT.NO_RECIPIENTS', 'No recipients found')}
                </span>
              </div>
            ) : null}

            {showAddButton ? (
              <div className={cn(styles['add-button-wrapper'], 'add-button-wrapper')}>
                <div className={cn(styles['add-button-inner'], 'add-button-inner')} onClick={handleAdd}>
                  <AddIcon />
                  <span>
                    {settlement
                      ? tr('FRONTEND.COMPONENTS.SELECT_RECIPIENT.NEW_SETTLEMENT', 'New external account')
                      : tr('FRONTEND.COMPONENTS.SELECT_RECIPIENT.NEW_RECIPIENT', 'New recipient')}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {isValidString(error) && (
        <div className={cn(surfaceStyles['selector-error'], styles['select-account-error'], 'select-account-error')}>
          {error}
        </div>
      )}
    </div>
  )
}

export default SelectRecipient
